import React,{useState,useEffect,useMemo,useCallback,useRef} from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import UsersForm from './UsersForm'
import './shop.css'

/* ------------------------------------------------------------------
   ICONS
   ------------------------------------------------------------------ */
const IconX=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>)
const IconSearch=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>)
const IconEye=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>)
const IconEyeOff=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 3l18 18"/><path d="M10.6 6.2A9.8 9.8 0 0 1 12 6c6.4 0 10 6 10 6a17 17 0 0 1-3.2 3.9"/><path d="M6.2 7.2A17 17 0 0 0 2 12s3.6 7 10 7a9.7 9.7 0 0 0 4-.8"/><path d="M9.9 10a3 3 0 0 0 4.2 4.2"/></svg>)
const IconAlert=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/></svg>)
const IconTrash=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M10 11v6M14 11v6"/></svg>)

/* ------------------------------------------------------------------
   THEME TOKENS FOR THE DATA GRID — see EShopProducts for the why.
   ------------------------------------------------------------------ */
const TOKEN_NAMES=[
  '--panel','--panel-raise','--line','--line-soft',
  '--ink','--ink-2','--ink-3','--accent','--accent-wash',
  '--mono','--sans','--step-0'
]

const readTokens=()=>{
  const out={}
  if (typeof window==='undefined'||!document.documentElement) return out
  const styles=window.getComputedStyle(document.documentElement)
  TOKEN_NAMES.forEach(name=>{ out[name]=String(styles.getPropertyValue(name)||'').trim() })
  return out
}

function useTableStyles(){
  const [tokens,setTokens]=useState(readTokens)

  useEffect(()=>{
    let frame=null
    const refresh=()=>{
      if (frame) window.cancelAnimationFrame(frame)
      frame=window.requestAnimationFrame(()=>setTokens(readTokens()))
    }

    const observer=new MutationObserver(refresh)
    observer.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme','class','style']})

    let media=null
    if (window.matchMedia){
      media=window.matchMedia('(prefers-color-scheme: dark)')
      if (media.addEventListener) media.addEventListener('change',refresh)
      else if (media.addListener) media.addListener(refresh)
    }

    refresh()

    return ()=>{
      if (frame) window.cancelAnimationFrame(frame)
      observer.disconnect()
      if (media){
        if (media.removeEventListener) media.removeEventListener('change',refresh)
        else if (media.removeListener) media.removeListener(refresh)
      }
    }
  },[])

  return useMemo(()=>{
    const t=tokens
    return {
      table:{ style:{ backgroundColor:t['--panel'], color:t['--ink'] } },
      tableWrapper:{ style:{ display:'block' } },
      head:{ style:{ color:t['--ink-3'] } },
      headRow:{
        style:{
          backgroundColor:t['--panel-raise'],
          color:t['--ink-3'],
          minHeight:'42px',
          borderBottomWidth:'1px',
          borderBottomColor:t['--line'],
          borderBottomStyle:'solid'
        }
      },
      headCells:{
        style:{
          paddingLeft:'12px',
          paddingRight:'12px',
          fontFamily:t['--mono'],
          fontSize:'10px',
          fontWeight:600,
          letterSpacing:'0.08em',
          textTransform:'uppercase',
          color:t['--ink-3']
        }
      },
      rows:{
        style:{
          backgroundColor:t['--panel'],
          color:t['--ink'],
          fontFamily:t['--sans'],
          fontSize:t['--step-0']||'0.875rem',
          minHeight:'52px',
          '&:not(:last-of-type)':{
            borderBottomWidth:'1px',
            borderBottomStyle:'solid',
            borderBottomColor:t['--line-soft']
          }
        },
        highlightOnHoverStyle:{
          backgroundColor:t['--accent-wash'],
          color:t['--ink'],
          borderBottomColor:t['--line-soft'],
          outlineWidth:'0px',
          outlineColor:t['--panel'],
          transitionDuration:'0.15s',
          transitionProperty:'background-color'
        }
      },
      cells:{ style:{ paddingLeft:'12px', paddingRight:'12px' } },
      pagination:{
        style:{
          backgroundColor:t['--panel'],
          color:t['--ink-3'],
          fontSize:'12px',
          minHeight:'50px',
          borderTopWidth:'1px',
          borderTopStyle:'solid',
          borderTopColor:t['--line']
        },
        pageButtonsStyle:{
          color:t['--ink-2'],
          fill:t['--ink-2'],
          backgroundColor:'transparent',
          '&:disabled':{ color:t['--ink-3'], fill:t['--ink-3'] },
          '&:hover:not(:disabled)':{ backgroundColor:t['--panel-raise'] },
          '&:focus':{ outline:'none', backgroundColor:t['--accent-wash'] }
        }
      },
      noData:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'], padding:'26px' } },
      progress:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'] } }
    }
  },[tokens])
}

/* A password should not sit in plain sight on a shared screen. */
function Secret({value,label}){
  const [open,setOpen]=useState(false)
  const text=String(value===undefined||value===null?'':value)
  if (!text) return <span className="sh-cell-mute">—</span>
  const masked='•'.repeat(Math.min(text.length,16))

  return (
    <span className="sh-secret">
      <span className="sh-secret-v" data-hidden={open?'0':'1'}>{open?text:masked}</span>
      <button
        type="button"
        className="sh-icon-btn"
        onClick={()=>setOpen(v=>!v)}
        aria-label={(open?'Απόκρυψη ':'Εμφάνιση ')+(label||'κωδικού')}
        aria-pressed={open}
      >
        {open?<IconEyeOff/>:<IconEye/>}
      </button>
    </span>
  )
}

/* Token-styled dialog. Escape closes, backdrop closes, full-bleed under 560px. */
function Sheet({open,title,eyebrow,subtitle,size,onClose,children}){
  useEffect(()=>{
    if (!open) return undefined
    const onKey=(e)=>{ if (e.key==='Escape') onClose() }
    document.addEventListener('keydown',onKey)
    return ()=>document.removeEventListener('keydown',onKey)
  },[open,onClose])

  /* Its own effect, keyed on `open` alone. onClose is a fresh arrow on every
     render of the screen, so the effect above tears down and rebuilds on each
     keystroke — a body-scroll lock in there would read back its own 'hidden'
     as the value to restore and leave the page frozen after close. */
  useEffect(()=>{
    if (!open) return undefined
    const prevOverflow=document.body.style.overflow
    document.body.style.overflow='hidden'
    return ()=>{ document.body.style.overflow=prevOverflow }
  },[open])

  if (!open) return null

  return (
    <div className="sh-back" onMouseDown={(e)=>{ if (e.target===e.currentTarget) onClose() }}>
      <div className={size?'sh-modal '+size:'sh-modal'} role="dialog" aria-modal="true" aria-label={title}>
        <div className="sh-modal-head">
          <div className="sh-modal-headline">
            {eyebrow?<span className="sh-modal-eyebrow">{eyebrow}</span>:null}
            <h2 className="sh-modal-title">{title}</h2>
            {subtitle?<div className="sh-modal-sub">{subtitle}</div>:null}
          </div>
          <button type="button" className="sh-modal-x" onClick={onClose} aria-label="Κλείσιμο"><IconX/></button>
        </div>
        <div className="sh-modal-body">{children}</div>
      </div>
    </div>
  )
}

/* "2025-08-31T00:00:00.000Z" -> "2025-08-31" */
const dayOf=(value)=>String(value===undefined||value===null?'':value).split('T')[0]

const isLive=(row)=>{
  const endDate=dayOf(row&&row.EndDate)
  const today=new Date().toISOString().split('T')[0]
  return today>endDate ? 0 : 1
}

/* The API sends EndDateDisplay pre-formatted; the original screen showed
   parts 2, 1 and 3 of it, so that reading is kept verbatim. */
const endLabel=(row)=>{
  const parts=String(row&&row.EndDateDisplay?row.EndDateDisplay:'').split(' ')
  const text=(parts[2]||'')+' '+(parts[1]||'')+' '+(parts[3]||'')
  return text.trim()?text:dayOf(row&&row.EndDate)
}

export default function EShopCustomers() {

    const [form,setForm]=useState(false)
    const [id,setId]=useState(0)
    const [isLoading,setIsLoading]=useState(true)
    const [data,setData]=useState([])
    const [sports,setSports]=useState([])
    const [record,setRecord]=useState({})
    const [show,setShow]=useState(false)

    /* inline feedback — this is what replaced alert() / window.confirm() */
    const [note,setNote]=useState(null)
    const [formNote,setFormNote]=useState(null)
    const [askDelete,setAskDelete]=useState(false)
    const [rowEnd,setRowEnd]=useState('')
    const [q,setQ]=useState('')
    const [state,setState]=useState('all')
    const [sportFlt,setSportFlt]=useState('0')

    /* ------------------------------------------------------------------
       BULK DELETE

       There is no bulk endpoint. admin/eshopscustomers/deleteorder takes
       exactly one order, so "delete 9" is nine requests — and they are
       sent one after another, never in parallel: nine simultaneous
       deletes against the same table is how you get half of them
       rejected and no way to tell which half.

       Because the run can therefore fail part-way, the outcome is
       counted rather than assumed. What comes back is "7 από 9
       διαγράφηκαν, 2 απέτυχαν", with the two named, not a success
       message covering a partial run.
       ------------------------------------------------------------------ */
    const [selected,setSelected]=useState([])
    const [selKey,setSelKey]=useState(false)      // toggling this clears the grid's selection
    const [bulkMode,setBulkMode]=useState('idle') // 'idle' | 'ask' | 'busy' | 'done'
    const [bulkDone,setBulkDone]=useState(0)
    const [bulkTotal,setBulkTotal]=useState(0)
    const [bulkReport,setBulkReport]=useState(null)
    const askRef=useRef(null)

    const tableStyles=useTableStyles()

    const columns = [
        {
            name: '#',
            selector: (row,index) =>  index+1,
            sortable: false,
            width:'56px'
        },
        {
            name: 'Κατάσταση',
            selector: row => isLive(row),
            cell: row => (
                <span className="sh-status">
                    <span className="sh-dot" data-tone={isLive(row)===1?'good':'crit'}></span>
                    <span className="sh-status-t">{isLive(row)===1?'Ενεργή':'Έληξε'}</span>
                </span>
            ),
            sortable: true,
            width:'130px'
        },
        {
            name: 'Όνομα',
            selector: row => row.Name,
            cell: row => <span className="sh-cell-strong">{row.Name}</span>,
            sortable: true,
            minWidth:'160px'
        },
        {
            name: 'Email',
            selector: row => row.Email,
            sortable: true,
            minWidth:'200px'
        },
        {
            name: 'Password',
            selector: row => row.Password,
            cell: row => <Secret value={row.Password} label={'κωδικού '+String(row.Name||'')}/>,
            sortable: false,
            minWidth:'170px'
        },
        {
            name: 'Product',
            selector: row => row.Title,
            sortable: true,
            minWidth:'170px'
        },
        {
            name: 'End Date',
            selector: row => dayOf(row.EndDate),
            cell: row => (
                <button
                    type="button"
                    className="sh-linkish"
                    onClick={()=>showModal(row.OrderID,row.Name,row.EndDate)}
                    aria-label={'Αλλαγή ημερομηνίας λήξης — '+String(row.Name||'')}
                >
                    {endLabel(row)}
                </button>
            ),
            sortable: true,
            minWidth:'160px'
        }
    ]


    useEffect(()=>{
        setIsLoading(true)
        loadData()
    },[form])


    const loadData=()=>{
        setIsLoading(true)
        axios.get(process.env.REACT_APP_API_SERVER+'admin/eshopscustomers',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
          setData(res.data.results)
          loadSports()
          setIsLoading(false)

        }).catch(err=>{
            setIsLoading(false)
            setNote({tone:'crit',text:'Οι συνδρομητές δεν φορτώθηκαν. Ελέγξτε τη σύνδεση και δοκιμάστε ξανά.'})
            console.log(err)
        })
      }


      const loadSports=()=>{
        axios.get(process.env.REACT_APP_API_SERVER+'admin/sports',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
          setSports(res.data.results)
        }).catch(err=>{
            console.log(err)
        })
      }

    const showForm=(id,a)=>{
        setForm(a)
        setId(id)
    }

    const handleClose=()=>{
        setShow(false)
        setAskDelete(false)
    }

    const showModal=(id,name,endDate)=>{
        let yourDate = new Date()
        let today=yourDate.toISOString().split('T')[0]
        setFormNote(null)
        setAskDelete(false)
        setRowEnd(dayOf(endDate))
        setRecord({
            OrderID:id,
            Name:name,
            EndDate:today

        })
        setShow(true)
    }

    const save=(e)=>{
        e.preventDefault()
        axios.post(process.env.REACT_APP_API_SERVER+'admin/eshopscustomers/newenddate',record,{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                }
            })
          .then(res=>{
            if (res.data.err==0){
                loadData()
                setShow(false)
                setNote({tone:'ok',text:res.data.msg})
            }else{
                setFormNote({tone:'crit',text:res.data.msg})
            }
        }).catch(err=>{
            setFormNote({tone:'crit',text:'Η αποθήκευση απέτυχε. Δοκιμάστε ξανά.'})
            console.log(err)
        })
    }

    const deleteRecord=()=>{
            axios.post(process.env.REACT_APP_API_SERVER+'admin/eshopscustomers/deleteorder',record,{
                headers: {
                    'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                    }
                })
              .then(res=>{
                if (res.data.err==0){
                    loadData()
                    setShow(false)
                    setAskDelete(false)
                    setNote({tone:'ok',text:res.data.msg})
                }else{
                    setFormNote({tone:'crit',text:res.data.msg})
                }
            }).catch(err=>{
                setFormNote({tone:'crit',text:'Η διαγραφή απέτυχε. Δοκιμάστε ξανά.'})
                console.log(err)
            })
    }

    /* ------------------------------------------------------------------
       BULK DELETE — selection, confirmation, sequential run, report
       ------------------------------------------------------------------ */

    /* Stable identity: the grid calls this from an effect, so a new
       function on every render would loop it. */
    const onSelect=useCallback((s)=>{
        const next=Array.isArray(s.selectedRows)?s.selectedRows:[]
        setSelected(next)
        /* Starting a fresh selection puts the previous run's report away —
           but only then, so the report survives the two selection resets
           that follow a run (the manual clear and the reload). */
        if (next.length>0){
            setBulkMode(m=>(m==='done'?'idle':m))
            setBulkReport(r=>(r?null:r))
        }
    },[])

    /* Nothing may be checked or unchecked while the run is in flight —
       the list of targets is fixed the moment the run starts. */
    const rowDisabled=useCallback(()=>bulkMode==='busy',[bulkMode])

    const clearSelection=()=>{
        setSelected([])
        setSelKey(v=>!v)
    }

    const closeBulk=()=>{
        setBulkMode('idle')
        setBulkReport(null)
        setBulkDone(0)
        setBulkTotal(0)
    }

    /* A row that scrolls out of the filtered view must not stay quietly
       selected — the count in the bar has to mean what it says. */
    useEffect(()=>{
        setSelected([])
        setSelKey(v=>!v)
        setBulkMode('idle')
        setBulkReport(null)
    },[q,state,sportFlt])

    const nameOf=(row)=>{
        const label=String((row&&row.Name)||'').trim()
        return label||('Παραγγελία #'+String((row&&row.OrderID)||'—'))
    }

    /* One order per request, awaited in turn. The payload is built exactly
       as the single-row delete builds it, so the server sees the identical
       shape either way. */
    /* An alertdialog nobody focuses is announced to nobody: a screen reader
       hears the checkbox it just toggled, not the confirmation that replaced
       the toolbar. Focus lands on the region itself rather than on the
       destructive button, so a held Enter cannot carry through into it, and
       Escape backs out the same way it does out of every other dialog here. */
    useEffect(()=>{
        if (bulkMode!=='ask') return undefined
        if (askRef.current) askRef.current.focus()
        const onKey=(e)=>{
            if (e.key==='Escape'||e.key==='Esc'){ e.stopPropagation(); setBulkMode('idle') }
        }
        document.addEventListener('keydown',onKey)
        return ()=>document.removeEventListener('keydown',onKey)
    },[bulkMode])

    const runBulkDelete=async()=>{
        /* The ask bar is replaced by the busy bar on the first click, but two
           clicks landing before that commit would start the run twice against
           the same nine orders. */
        if (bulkMode==='busy') return

        const targets=selected.slice()
        if (targets.length===0) return

        const today=new Date().toISOString().split('T')[0]

        setBulkMode('busy')
        setBulkReport(null)
        setBulkDone(0)
        setBulkTotal(targets.length)

        let ok=0
        const failed=[]

        for (let i=0;i<targets.length;i++){
            const row=targets[i]
            const payload={OrderID:row.OrderID,Name:row.Name,EndDate:today}

            try{
                const res=await axios.post(process.env.REACT_APP_API_SERVER+'admin/eshopscustomers/deleteorder',payload,{
                    headers: {
                        'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                        }
                    })
                if (res.data.err==0){
                    ok++
                }else{
                    failed.push({name:nameOf(row),why:String(res.data.msg||'Απορρίφθηκε από τον server')})
                }
            }catch(err){
                failed.push({name:nameOf(row),why:'Το αίτημα δεν ολοκληρώθηκε'})
                console.log(err)
            }

            setBulkDone(i+1)
        }

        setBulkReport({total:targets.length,ok:ok,fail:failed.length,failed:failed})
        setBulkMode('done')
        clearSelection()
        loadData()
    }

    const rows=Array.isArray(data)?data:[]
    const live=rows.filter(item=>isLive(item)===1).length
    const bySport=rows.some(item=>item&&item.SportID!==undefined&&item.SportID!==null)

    const shown=useMemo(()=>{
      const needle=q.trim().toLowerCase()
      return rows.filter(item=>{
        if (state==='live'&&isLive(item)!==1) return false
        if (state==='past'&&isLive(item)===1) return false
        if (bySport&&sportFlt!=='0'&&String(item.SportID)!==String(sportFlt)) return false
        if (!needle) return true
        const hay=(String(item.Name||'')+' '+String(item.Email||'')+' '+String(item.Title||'')).toLowerCase()
        return hay.indexOf(needle)>=0
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[rows,q,state,sportFlt,bySport])

    const bulkCount=selected.length
    /* If the selection emptied under a pending confirmation, drop back — a
       dialog that says "delete 0" is a dialog nobody should be shown. */
    const bulkView=(bulkMode==='ask'&&bulkCount===0)?'idle':bulkMode
    const bulkBarOpen=bulkView==='busy'||bulkView==='done'||bulkCount>0
    const bulkPct=bulkTotal>0?Math.round((bulkDone/bulkTotal)*100):0

    return (
        <div className="sh-page">

            {note
              ? <div className="sh-note" data-tone={note.tone} role="status">
                  <span className="sh-note-text">{note.text}</span>
                  <button type="button" className="sh-note-x" aria-label="Κλείσιμο" onClick={()=>setNote(null)}><IconX/></button>
                </div>
              : null}

            {form
              ? <Panel
                  title="Προσθήκη/Επεξεργασία User"
                  actions={<Btn onClick={()=>{showForm(0,false)}}>Users List</Btn>}
                >
                  <UsersForm id={id} showForm={showForm} setForm={setForm} loadData={loadData}/>
                </Panel>
              : null}

            <Panel
              title="EShop Customers"
              subtitle={isLoading
                ? 'Φόρτωση…'
                : rows.length===0
                  ? 'Καμία συνδρομή'
                  : rows.length+(rows.length===1?' συνδρομή':' συνδρομές')+' — '+live+' σε ισχύ'}
              actions={
                <div className="sh-tools">
                  {rows.length>0
                    ? <>
                        <button type="button" className="toggle" aria-pressed={state==='all'} onClick={()=>setState('all')}>Όλες</button>
                        <button type="button" className="toggle" aria-pressed={state==='live'} onClick={()=>setState('live')}>Σε ισχύ</button>
                        <button type="button" className="toggle" aria-pressed={state==='past'} onClick={()=>setState('past')}>Ληγμένες</button>
                        {bySport
                          ? <select
                              className="sh-filter"
                              value={sportFlt}
                              aria-label="Φίλτρο αθλήματος"
                              onChange={(e)=>setSportFlt(e.target.value)}
                            >
                              <option value="0">[All Sports]</option>
                              {sports.map(item=>(
                                <option key={item.SportID} value={item.SportID}>{item.SportName}</option>
                              ))}
                            </select>
                          : null}
                        <span className="sh-search">
                          <IconSearch/>
                          <input
                            className="sh-search-input"
                            type="search"
                            value={q}
                            placeholder="Όνομα, email ή προϊόν"
                            aria-label="Αναζήτηση συνδρομητή"
                            onChange={(e)=>setQ(e.target.value)}
                          />
                        </span>
                      </>
                    : null}
                </div>
              }
            >
              {bulkBarOpen
                ? bulkView==='busy'
                  ? <div className="sh-bulk" data-mode="busy" role="status" aria-live="polite">
                      <div className="sh-bulk-t">Διαγραφή σε εξέλιξη</div>
                      <div className="sh-bulk-s">
                        <b>{bulkDone} από {bulkTotal}</b> — τα αιτήματα στέλνονται ένα-ένα, όχι μαζί.
                        Μην κλείσετε τη σελίδα μέχρι να τελειώσει.
                      </div>
                      <div
                        className="sh-prog"
                        role="progressbar"
                        aria-label="Πρόοδος μαζικής διαγραφής"
                        aria-valuemin={0}
                        aria-valuemax={bulkTotal}
                        aria-valuenow={bulkDone}
                      >
                        <span className="sh-prog-fill" style={{width:bulkPct+'%'}}></span>
                      </div>
                    </div>
                  : bulkView==='done'&&bulkReport
                    ? <div className="sh-bulk" data-mode="done" role="status">
                        <div className="sh-bulk-t">
                          {bulkReport.fail===0
                            ? 'Η μαζική διαγραφή ολοκληρώθηκε'
                            : <><IconAlert/> Η μαζική διαγραφή ολοκληρώθηκε μερικώς</>}
                        </div>
                        <div className="sh-bulk-s">
                          {bulkReport.fail===0
                            ? <><b>{bulkReport.ok} από {bulkReport.total}</b>{' '}
                                {bulkReport.total===1?'συνδρομή διαγράφηκε':'συνδρομές διαγράφηκαν'}.</>
                            : bulkReport.ok===0
                              ? <>Καμία από τις <b>{bulkReport.total}</b> συνδρομές δεν διαγράφηκε —{' '}
                                  <b>{bulkReport.fail}</b> απέτυχαν.</>
                              : <><b>{bulkReport.ok} από {bulkReport.total}</b> διαγράφηκαν,{' '}
                                  <b>{bulkReport.fail}</b> απέτυχαν.</>}
                        </div>
                        {bulkReport.fail>0
                          ? <>
                              <div className="sh-bulk-s">Δεν διαγράφηκαν:</div>
                              <ul className="sh-bulk-fails">
                                {bulkReport.failed.map((f,i)=>(
                                  <li key={i}>{f.name} — {f.why}</li>
                                ))}
                              </ul>
                            </>
                          : null}
                        <div className="sh-bulk-a">
                          <Btn onClick={closeBulk}>Εντάξει</Btn>
                        </div>
                      </div>
                    : bulkView==='ask'
                      ? <div
                          className="sh-bulk"
                          data-mode="ask"
                          role="alertdialog"
                          aria-label="Επιβεβαίωση μαζικής διαγραφής"
                          ref={askRef}
                          tabIndex={-1}
                        >
                          <div className="sh-bulk-t">
                            <IconAlert/> Διαγραφή {bulkCount} {bulkCount===1?'συνδρομής':'συνδρομών'};
                          </div>
                          <div className="sh-bulk-s">
                            Θα διαγραφούν <b>{bulkCount}</b>{' '}
                            {bulkCount===1?'παραγγελία':'παραγγελίες'} και οι αντίστοιχοι managers
                            χάνουν αμέσως την πρόσβασή τους. Τα αιτήματα στέλνονται ένα-ένα, οπότε αν
                            κάποιο αποτύχει θα δείτε ακριβώς ποιο. Η ενέργεια δεν αναιρείται.
                          </div>
                          <div className="sh-bulk-a">
                            <Btn onClick={()=>setBulkMode('idle')}>Άκυρο</Btn>
                            <Btn className="sh-btn-crit" onClick={runBulkDelete}>
                              <IconTrash/><span>Οριστική διαγραφή {bulkCount}</span>
                            </Btn>
                          </div>
                        </div>
                      : <div className="sh-bulk" data-mode="idle">
                          <span className="sh-bulk-n">
                            <b>{bulkCount}</b>
                            <span>{bulkCount===1?'επιλεγμένη συνδρομή':'επιλεγμένες συνδρομές'}</span>
                          </span>
                          <span className="sh-bulk-a">
                            <Btn onClick={clearSelection}>Καθαρισμός επιλογής</Btn>
                            <Btn className="sh-btn-crit" onClick={()=>setBulkMode('ask')}>
                              <IconTrash/><span>Διαγραφή {bulkCount}</span>
                            </Btn>
                          </span>
                        </div>
                : null}

              {isLoading
                ? <div className="sh-sk" aria-hidden="true">
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                  </div>
                : rows.length===0
                  ? <div className="sh-empty">
                      <div className="sh-empty-t">Καμία συνδρομή ακόμη</div>
                      <div className="sh-empty-s">
                        Εδώ εμφανίζεται κάθε manager που έχει αγοράσει πακέτο από το eshop,
                        μαζί με την ημερομηνία λήξης της πρόσβασής του.
                      </div>
                      <div className="sh-empty-a"><Btn onClick={loadData}>Ανανέωση</Btn></div>
                    </div>
                  : shown.length===0
                    ? <div className="sh-empty">
                        <div className="sh-empty-t">Καμία συνδρομή δεν ταιριάζει</div>
                        <div className="sh-empty-s">Καθαρίστε τα φίλτρα για να δείτε και τις {rows.length} συνδρομές.</div>
                        <div className="sh-empty-a">
                          <Btn onClick={()=>{setQ('');setState('all');setSportFlt('0')}}>Καθαρισμός φίλτρων</Btn>
                        </div>
                      </div>
                    : <>
                        <div className="sh-count">
                          <span className="sh-count-n num">{shown.length}</span>
                          <span>
                            {state==='live'?'συνδρομές σε ισχύ':state==='past'?'ληγμένες συνδρομές':'συνδρομές'}
                            {q.trim()?' που ταιριάζουν με την αναζήτηση':''}
                          </span>
                        </div>
                        <div className="sh-table">
                          <DataTable
                            columns={columns}
                            data={shown}
                            keyField="OrderID"
                            customStyles={tableStyles}
                            highlightOnHover
                            persistTableHead
                            selectableRows
                            selectableRowsHighlight
                            selectableRowsNoSelectAll={false}
                            onSelectedRowsChange={onSelect}
                            clearSelectedRows={selKey}
                            selectableRowDisabled={rowDisabled}
                            pagination
                            paginationPerPage={25}
                            paginationRowsPerPageOptions={[25,50,100,200]}
                            noDataComponent={<div className="sh-empty-s">Καμία συνδρομή.</div>}
                          />
                        </div>
                      </>}
            </Panel>

            <Sheet
              open={show}
              eyebrow="Συνδρομή"
              title={'Change EndDate'}
              subtitle={record.Name?String(record.Name):undefined}
              onClose={handleClose}
            >
              {formNote
                ? <div className="sh-note" data-tone={formNote.tone} role="alert">
                    <span className="sh-note-text">{formNote.text}</span>
                    <button type="button" className="sh-note-x" aria-label="Κλείσιμο" onClick={()=>setFormNote(null)}><IconX/></button>
                  </div>
                : null}

              <form className="sh-form" onSubmit={save}>

                <div className="sh-facts">
                  <div className="sh-fact">
                    <span className="sh-fact-k">Συνδρομητής</span>
                    <span className="sh-fact-v">{record.Name}</span>
                  </div>
                  <div className="sh-fact">
                    <span className="sh-fact-k">Τρέχουσα λήξη</span>
                    <span className="sh-fact-v">{rowEnd||'—'}</span>
                  </div>
                </div>

                <div className="sh-field">
                  <label className="sh-label" htmlFor="cu-end">End Date for {record.Name}</label>
                  <input
                    id="cu-end"
                    className="sh-input"
                    type="date"
                    required
                    value={record.EndDate}
                    onChange={(e)=>setRecord({...record,EndDate:e.target.value})}
                  />
                  <div className="sh-hint">
                    Η πρόσβαση του manager κλείνει στο τέλος αυτής της ημέρας.
                  </div>
                </div>

                {askDelete
                  ? <div className="sh-confirm" role="group" aria-label="Επιβεβαίωση διαγραφής">
                      <div className="sh-confirm-t">Διαγραφή της συνδρομής του «{record.Name}»;</div>
                      <div className="sh-confirm-s">
                        Η παραγγελία διαγράφεται και ο manager χάνει αμέσως την πρόσβασή του.
                        Η ενέργεια δεν αναιρείται.
                      </div>
                      <div className="sh-actions">
                        <Btn onClick={()=>setAskDelete(false)}>Άκυρο</Btn>
                        <span className="sh-actions-end">
                          <Btn className="sh-btn-crit" onClick={deleteRecord}>Οριστική διαγραφή</Btn>
                        </span>
                      </div>
                    </div>
                  : <div className="sh-actions">
                      <Btn className="sh-btn-crit" onClick={()=>setAskDelete(true)}>Διαγραφή</Btn>
                      <span className="sh-actions-end">
                        <Btn onClick={handleClose}>Άκυρο</Btn>
                        <Btn variant="accent" type="submit">Αποθήκευση</Btn>
                      </span>
                    </div>}

              </form>
            </Sheet>
        </div>
    )
}
